import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Flyout from 'Components/UI/Flyout';
import closeModal from 'Helpers/ModalHelper';
import { withStyles } from '@material-ui/core/styles';
import { Divider, Typography } from '@material-ui/core';
import { HistoryOutlined } from '@material-ui/icons';
import fr from 'facet-rest';
import { QueryVal } from 'Helpers/Query';
import useCollection from 'Hooks/useCollection';
import MetaCollectionFormAccordion from 'Components/UI/Meta/MetaForm/Components/MetaCollectionFormAccordion';
import moment from 'moment';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { isEqual } from 'lodash';
import { fullDateTime } from 'Consts/DateFormats';
import { windows } from 'Helpers/Utils';
import { getHistoryOverrideForResource, getHistoryTitleGeneratorForResource } from 'Resources/History';

const styles = theme => ({
  flyoutRoot: {
    width: theme.dimensions.xl,
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
  },
  headerIcon: {
    alignSelf: 'center',
    fill: theme.palette.facet.primaryBlue,
  },
  title: {
    color: theme.palette.facet.primaryBlue,
    padding: theme.spacing(2),
  },
  loader: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  closeButton: {
    width: '25px',
    minWidth: '25px',
    padding: '0px',
  },
});

// generates an override that only displays fields that have changed
const diffOverride = (left, right) => {
  const leftAttrs = left.attributes();
  const rightAttrs = right.attributes();

  const changedFields = Object.keys(leftAttrs).filter(field => !isEqual(leftAttrs[field], rightAttrs[field]));

  const override = getHistoryOverrideForResource(left);
  const titleGenerator = getHistoryTitleGeneratorForResource(left);

  return fr.metaOverride({ base: override })
    .nameOverride(name => !changedFields.includes(name), { hidden: true })
    .nameOverride('updatedAt', { _titleGenerator: titleGenerator, hidden: true });
};

// returns an array of resources with special override applied to each resource
const getResourceDiff = (collection) => {
  const resources = collection.resources().list();
  const resourcePairs = windows(resources, 2);

  const first = resources[resources.length - 1];

  return resourcePairs.map(([left, right]) => {
    const diff = diffOverride(left, right);
    return left.overrideMeta(diff);
  }).concat(first.overrideMeta(getHistoryOverrideForResource(first)));
};

const HistoryFlyout = ({ classes }) => {
  const { collection, setCollection } = useCollection();
  const link = atob(QueryVal('link'));
  const title = atob(QueryVal('title'));

  const [showDiff, setShowDiff] = useState(false);
  const [response, setResponse] = useState(null);

  const titleGenerator = (resource) => {
    const metaField = resource.meta().field('updatedAt');
    const titleGeneratorField = metaField.attr('_titleGenerator');

    const name = titleGeneratorField ? titleGeneratorField(resource) : 'No Name Set';
    const date = moment(resource.attr('updatedAt')).format(fullDateTime);

    return `${date} ${name}`;
  };

  const setDefaultCollectionFromResponse = (resp) => {
    const responseCollection = fr.collection(resp);
    const override = getHistoryOverrideForResource(responseCollection.resources().head());
    if (override) {
      setCollection(fr.collection(resp, override));
    } else {
      setCollection(responseCollection);
    }
  };

  const toggleDiff = () => {
    const nextState = !showDiff;

    if (nextState) {
      const coll = fr.collection(response);
      const resourceDiff = getResourceDiff(coll);

      resourceDiff.forEach(resource => coll.resources().updateItem(resource));

      setCollection(coll);
    } else {
      setDefaultCollectionFromResponse(response);
    }

    setShowDiff(nextState);
  };

  const toggleDisabled = collection?.resources()?.list().length <= 1 || !collection;

  useEffect(() => {
    if (link) {
      fr.http.Get(link)
        .then((resp) => {
          setResponse(resp);
          setDefaultCollectionFromResponse(resp);
        });
    }
  }, [link]);
  let historyRecords = <div>Loading Records...</div>;

  if (collection != null) {
    if (collection.resources().length() === 0) {
      historyRecords = <div>This item has no history yet.</div>;
    } else {
      historyRecords = (<MetaCollectionFormAccordion
        collection={collection}
        titleGenerator={titleGenerator}
        FormProps={{ readonly: true, hideEmptyFields: true }}
        AccordionProps={{ square: true, elevation: 1, className: classes.accordion }}
      />);
    }
  }


  return (
    <Flyout
      open
      anchor="right"
      onClose={closeModal}
      classes={{
        paper: classes.flyoutRoot,
      }}
    >
      <div className={classes.header}>
        <HistoryOutlined className={classes.headerIcon} />
        <Typography
          className={classes.title}
          variant="h2"
        >
          {title}
        </Typography>
      </div>
      <FormControlLabel
        control={<Switch
          checked={showDiff}
          onChange={toggleDiff}
          name="toggleDifferences"
          disabled={toggleDisabled}
        />}
        label="Only show fields that have changed"
      />
      <Divider />
      {historyRecords}
    </Flyout>
  );
};

HistoryFlyout.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(HistoryFlyout);
